/**
 * Palette ranking (BUILD-PLAN 3.6): text match first (with a dental synonym
 * table), then per-user frequency and recency. Pure and fast: the whole
 * command list is scored on every keystroke inside the 50 ms budget.
 *
 * Old-PMS words: staff coming from Dentrix, Open Dental or Eaglesoft type
 * the words they know ("continuing care", "family file", "appt book"). The
 * skins' term maps already say what each of those means in Velora, so a
 * query holding one of those phrases is also scored with the phrase swapped
 * for Velora's word (IMP-17 item 3, platform:B3).
 */
import { VELORA_TERMS, type TermKey, type TermMap } from '@velora/contracts/skins/terms';
import { terms as dentrixTerms } from '@velora/contracts/skins/terms/dentrix';
import { terms as eaglesoftTerms } from '@velora/contracts/skins/terms/eaglesoft';
import { terms as openDentalTerms } from '@velora/contracts/skins/terms/opendental';

/** Dental jargon: a typed word also matches these words. */
export const SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  recare: ['recall', 'hygiene', 'due'],
  recall: ['recare', 'hygiene'],
  hygiene: ['recare', 'recall', 'prophy'],
  srp: ['d4341', 'd4342', 'scaling', 'root', 'planing', 'perio'],
  bwx: ['bitewing', 'bitewings', 'xray', 'imaging'],
  bitewing: ['bwx', 'xray'],
  pano: ['panoramic', 'xray', 'imaging'],
  fmx: ['full', 'mouth', 'xray', 'imaging'],
  xray: ['imaging', 'radiograph', 'bwx', 'pano'],
  np: ['new', 'patient'],
  crown: ['d27', 'crn'],
  crn: ['crown'],
  bridge: ['fpd'],
  fpd: ['bridge'],
  ledger: ['billing', 'balance', 'payments'],
  balance: ['ledger', 'billing'],
  text: ['sms', 'message', 'communications', 'inbox'],
  sms: ['text', 'message', 'communications'],
  inbox: ['communications', 'messages', 'text'],
  calendar: ['schedule', 'appointments'],
  appointment: ['schedule', 'appt'],
  appt: ['appointment', 'schedule'],
  chart: ['charting', 'tooth', 'teeth'],
  tooth: ['chart', 'charting'],
  dashboard: ['home', 'huddle'],
  huddle: ['home', 'dashboard'],
  office: ['location', 'clinic'],
  location: ['office', 'clinic'],
  clinic: ['office', 'location'],
  ai: ['assistant', 'ask'],
  addon: ['marketplace', 'module'],
  store: ['marketplace'],
};

export interface Rankable {
  id: string;
  label: string;
  keywords?: readonly string[];
  hint?: string;
  /** Static weight; core navigation ranks above rarely used actions. */
  weight?: number;
}

export interface UsageEntry {
  count: number;
  last: number;
}
export type UsageStats = Readonly<Record<string, UsageEntry>>;

function words(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Subsequence match ("schd" in "schedule"), scored by compactness. */
function fuzzy(needle: string, hay: string): number {
  let j = 0;
  let first = -1;
  for (let i = 0; i < hay.length && j < needle.length; i++) {
    if (hay[i] === needle[j]) {
      if (first < 0) first = i;
      j++;
    }
  }
  if (j < needle.length) return 0;
  return needle.length / (hay.length - first + 1);
}

function tokenScore(token: string, labelWords: string[], extraWords: string[]): number {
  let best = 0;
  for (const w of labelWords) {
    if (w === token) best = Math.max(best, 10);
    else if (w.startsWith(token)) best = Math.max(best, 8);
    else if (w.includes(token)) best = Math.max(best, 4);
  }
  for (const w of extraWords) {
    if (w === token) best = Math.max(best, 7);
    else if (w.startsWith(token)) best = Math.max(best, 6);
    else if (token.length >= 3 && w.includes(token)) best = Math.max(best, 3);
  }
  if (best === 0) {
    const f = Math.max(0, ...labelWords.map((w) => fuzzy(token, w)));
    if (f > 0.5) best = 2 * f;
  }
  return best;
}

/* ---------- old-PMS phrase aliases ---------- */

export interface PmsAlias {
  /** Product name shown in the hint ("Dentrix"). */
  skin: string;
  /** The skin's wording, as shown ("Continuing Care"). */
  phrase: string;
  /** `phrase` split into lower-case words. */
  words: readonly string[];
  /** Velora's wording for the same thing ("Recall"). */
  velora: string;
}

/** Colloquial names the term maps do not hold (window titles people say). */
const EXTRA_ALIASES: ReadonlyArray<{ skin: string; phrase: string; velora: string }> = [
  { skin: 'Dentrix', phrase: 'Appt Book', velora: VELORA_TERMS.schedule },
  { skin: 'Dentrix', phrase: 'Practice Defaults', velora: 'Practice settings' },
  { skin: 'Open Dental', phrase: 'Practice Setup', velora: 'Practice settings' },
];

/** Builds the alias list from skin term maps; exported for tests. */
export function buildPmsAliases(
  maps: ReadonlyArray<{ skin: string; terms: TermMap }>,
  extra: ReadonlyArray<{ skin: string; phrase: string; velora: string }> = [],
): PmsAlias[] {
  const out: PmsAlias[] = [];
  const seen = new Set<string>();
  const add = (skin: string, phrase: string, velora: string) => {
    const clean = phrase.replace(/&/g, '');
    const w = words(clean);
    // Two-letter codes ("ID", "NP") match far too much; synonyms cover NP.
    if (w.length === 0 || (w.length === 1 && (w[0] ?? '').length < 3)) return;
    if (w.join(' ') === words(velora).join(' ')) return;
    const key = `${skin}|${w.join(' ')}|${velora.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ skin, phrase: clean, words: w, velora });
  };
  for (const { skin, terms } of maps) {
    for (const [key, phrase] of Object.entries(terms) as Array<[TermKey, string | undefined]>) {
      // Menu labels ("&File") are chrome, not places to go.
      if (!phrase || key.startsWith('menu')) continue;
      add(skin, phrase, VELORA_TERMS[key]);
    }
  }
  for (const e of extra) add(e.skin, e.phrase, e.velora);
  // Longest phrase first, so "family file" wins over "family".
  return out.sort((a, b) => b.words.length - a.words.length);
}

export const PMS_ALIASES: readonly PmsAlias[] = buildPmsAliases(
  [
    { skin: 'Dentrix', terms: dentrixTerms },
    { skin: 'Open Dental', terms: openDentalTerms },
    { skin: 'Eaglesoft', terms: eaglesoftTerms },
  ],
  EXTRA_ALIASES,
);

/**
 * Where `alias` sits in `tokens`, or -1. The query's last word may be a
 * prefix of the phrase's last word ("continuing ca") so the match shows
 * while the person is still typing.
 */
function aliasAt(tokens: readonly string[], alias: PmsAlias): number {
  const n = alias.words.length;
  for (let i = 0; i + n <= tokens.length; i++) {
    let ok = true;
    for (let k = 0; k < n && ok; k++) {
      const t = tokens[i + k] ?? '';
      const w = alias.words[k] ?? '';
      const last = k === n - 1 && i + k === tokens.length - 1;
      ok = last ? t.length >= 2 && w.startsWith(t) : t === w;
    }
    if (ok) return i;
  }
  return -1;
}

/** Queries with each matched old-PMS phrase swapped for Velora's word. */
function aliasRewrites(
  query: string,
  aliases: readonly PmsAlias[],
): Array<{ query: string; alias: PmsAlias }> {
  const tokens = words(query);
  const out: Array<{ query: string; alias: PmsAlias }> = [];
  for (const alias of aliases) {
    const at = aliasAt(tokens, alias);
    if (at < 0) continue;
    const rewritten = [
      ...tokens.slice(0, at),
      ...words(alias.velora),
      ...tokens.slice(at + alias.words.length),
    ].join(' ');
    out.push({ query: rewritten, alias });
  }
  return out;
}

/**
 * The old-PMS phrase that made `item` match `query`, when it matched only
 * through one (for a "Dentrix: Continuing Care" hint). Null otherwise.
 */
export function aliasMatch(
  query: string,
  item: Rankable,
  aliases: readonly PmsAlias[] = PMS_ALIASES,
): PmsAlias | null {
  if (directScore(query, item) > 0) return null;
  for (const r of aliasRewrites(query, aliases)) {
    if (directScore(r.query, item) > 0) return r.alias;
  }
  return null;
}

/**
 * Text relevance of `query` for an item, 0 when any query word fails to
 * match (all words must match, directly, through a synonym, or after an
 * old-PMS phrase is swapped for Velora's word).
 */
export function textScore(
  query: string,
  item: Rankable,
  aliases: readonly PmsAlias[] = PMS_ALIASES,
): number {
  return scoreWith(query, aliasRewrites(query, aliases), item);
}

function scoreWith(
  query: string,
  rewrites: ReadonlyArray<{ query: string }>,
  item: Rankable,
): number {
  let best = directScore(query, item);
  for (const r of rewrites) {
    // Slightly below a direct hit, so a command literally named with the
    // typed words still leads.
    best = Math.max(best, directScore(r.query, item) * 0.95);
  }
  return best;
}

function directScore(query: string, item: Rankable): number {
  const tokens = words(query);
  if (tokens.length === 0) return 1;
  const labelWords = words(item.label);
  const extraWords = [...(item.keywords ?? []).flatMap(words), ...words(item.hint ?? '')];
  let total = 0;
  for (const t of tokens) {
    let s = tokenScore(t, labelWords, extraWords);
    if (s === 0) {
      for (const syn of SYNONYMS[t] ?? []) {
        s = Math.max(s, tokenScore(syn, labelWords, extraWords) * 0.8);
      }
    }
    if (s === 0) return 0;
    total += s;
  }
  // Whole-label prefix match is the strongest signal.
  if (item.label.toLowerCase().startsWith(query.trim().toLowerCase())) total += 5;
  return total;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Frequency and recency boost, bounded so text relevance still leads. */
export function usageBoost(usage: UsageEntry | undefined, now: number): number {
  if (!usage) return 0;
  const ageDays = Math.max(0, (now - usage.last) / DAY_MS);
  const recency = ageDays < 1 ? 3 : ageDays < 7 ? 2 : ageDays < 30 ? 1 : 0;
  return Math.min(4, Math.log2(1 + usage.count)) + recency;
}

export function rank<T extends Rankable>(
  query: string,
  items: readonly T[],
  usage: UsageStats,
  now: number,
): T[] {
  const scored: Array<{ item: T; score: number; i: number }> = [];
  const rewrites = aliasRewrites(query, PMS_ALIASES);
  items.forEach((item, i) => {
    const s = scoreWith(query, rewrites, item);
    if (s <= 0) return;
    scored.push({ item, score: s + usageBoost(usage[item.id], now) + (item.weight ?? 0), i });
  });
  scored.sort((a, b) => b.score - a.score || a.i - b.i);
  return scored.map((s) => s.item);
}

/** Records one use; keeps the 200 most recent entries. */
export function recordUse(usage: UsageStats, id: string, now: number): UsageStats {
  const prev = usage[id];
  const next: Record<string, UsageEntry> = {
    ...usage,
    [id]: { count: (prev?.count ?? 0) + 1, last: now },
  };
  const entries = Object.entries(next);
  if (entries.length <= 200) return next;
  entries.sort((a, b) => b[1].last - a[1].last);
  return Object.fromEntries(entries.slice(0, 200));
}
